/**
 * A field for a short numeric code, rather than a text box that happens to
 * hold one.
 *
 * One `<input>`, not six. A segmented control looks tidier and behaves worse:
 * it fights password managers and SMS autofill, it makes paste and backspace
 * into special cases, and on Android the per-box focus juggling drops
 * keystrokes. What actually matters — a numeric keypad, one-time-code
 * autofill, digits that do not reorder in a Persian line, and paste that just
 * works — is available on one properly configured input, and letter-spacing
 * gives the same "boxes" reading without the behaviour.
 *
 * Everything typed is normalised: Persian and Arabic-Indic digits become
 * ASCII, and anything that is not a digit is dropped rather than shown and
 * then rejected. Somebody pasting «۱۲۳۴۵۶» or `123 456` gets what they meant.
 */
import { forwardRef } from 'react'
import TextField from '@mui/material/TextField'

import { toCanonicalDigits } from '../numerals'

/**
 * The typed code, reduced to at most `length` ASCII digits.
 *
 * The digit conversion is the product-wide one in `numerals`, not a private
 * table: a code and a price are the same problem, and two implementations of
 * it are two things to keep in step.
 */
export function normalizeCode(raw: string, length: number): string {
  return toCanonicalDigits(raw).replace(/[^\d]/g, '').slice(0, length)
}

export const OtpInput = forwardRef<HTMLInputElement, {
  value: string
  onChange: (value: string) => void
  /** Fired once the field holds a complete code, however it got there. */
  onComplete?: (value: string) => void
  length: number
  label: string
  error?: boolean
  helperText?: string
  disabled?: boolean
  autoFocus?: boolean
}>(function OtpInput(
  { value, onChange, onComplete, length, label, error, helperText, disabled, autoFocus },
  ref,
) {
  return (
    <TextField
      inputRef={ref}
      label={label}
      value={value}
      onChange={(event) => {
        const next = normalizeCode(event.target.value, length)
        onChange(next)
        if (next.length === length) onComplete?.(next)
      }}
      error={error}
      helperText={helperText}
      disabled={disabled}
      autoFocus={autoFocus}
      fullWidth
      slotProps={{
        htmlInput: {
          // `dir` and `unicode-bidi` as attributes and inline style: the app's
          // emotion cache flips `direction` in `sx`, so the one place it must
          // not be flipped is the one place it is not written there.
          dir: 'ltr',
          inputMode: 'numeric',
          autoComplete: 'one-time-code',
          // `text`, not `number`: a number input hides the value behind
          // spinners, drops leading zeros and refuses paste in some browsers.
          type: 'text',
          // Deliberately no `maxLength`. The browser applies it to the raw
          // string before any of our code sees it, so pasting a code somebody
          // copied as "12 34 56" arrives truncated to "12 34 " and normalises
          // to four digits. The length cap belongs in `normalizeCode`, after
          // the separators are gone.
          pattern: '[0-9]*',
          'aria-label': label,
          style: {
            direction: 'ltr',
            unicodeBidi: 'isolate',
            textAlign: 'center',
            letterSpacing: '0.6em',
            // The trailing letter-space would push the run off centre.
            textIndent: '0.6em',
            fontSize: '1.5rem',
            fontFamily: 'monospace',
            paddingBlock: '0.6rem',
          },
        },
      }}
    />
  )
})
